import { Component, OnInit } from '@angular/core';
import { CompanyLogo } from '../../models/company-logos.model';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UntypedFormBuilder } from '@angular/forms';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { CompanyLogosService } from '../../services/company-logos.service';
import { WorkLocationsService } from '@app/modules/work-locations/services/work-locations.service';
import { finalize } from 'rxjs';
import moment from 'moment';
import { routes } from '@app/consts';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { base64ToFile } from 'ngx-image-cropper';

@Component({
  selector: 'app-company-logo-editor',
  templateUrl: './company-logo-editor.component.html',
  styleUrls: ['./company-logo-editor.component.scss']
})
export class CompanyLogoEditorComponent implements OnInit {
  formId: string ='frm_zdqTAxaidsmCUG';
  getFormData: boolean = false;
  formValid: boolean = false;
  formData: any;

  active: boolean = false;
  isLoading: boolean;
  companyLogoId: string;
  companyLogo: CompanyLogo;

  public columns: any[] = [
    {field: 'name', title: 'Name', dataType: "String"},
    { field: "streetAddress", title: "Street Address", tableId: "tfi_WlStreetAddress", dataType: "String"},
    { field: "city", title: "City", dataType: "String", tableId: "tfi_WlCity" },
    { field: "province", title: "Province", dataType: "String", tableId: "tfi_WlProvince" },
    { field: "zip", title: "Zip", dataType: "String", tableId: "tfi_WlZip" },
    { field: "region", subField: 'text', title: "Region", dataType: "String", tableId: "" },
    { field: "country", subField: 'name', title: "Country", dataType: "String", tableId: "tfi_WlCountry" },
    { field: "timezone", subField: 'text', title: "Timezone", dataType: "String", tableId: "" },
    { field: "companyLogos", subField: 'name', title: "stylingColor", tableId: "" },
    { field: "startDate", title: "Start Date", type: "date", dataType: "Date", tableId: "" },
    { field: "endDate", title: "End Date", type: "date", dataType: "Date", tableId: "" },
  ];

  sortableColumns: any[] = [
    { field: "name", sortValue: "name"},
    { field: "streetAddress", sortValue: "streetAddress" },
    { field: "city", sortValue: "city" },
    { field: "province", sortValue: "province" },
    { field: "zip", sortValue: "zip" },
    { field: "region", sortValue: "region.text" },
    { field: "country", sortValue: "country.name" },
    { field: "timezone", sortValue: "timezone.text" },
    { field: "startDate", sortValue: "startDate" },
    { field: "endDate", sortValue: "endDate" },
    { field: "stylingColor", sortValue: "stylingColor" },
  ];

  public filterCategories: any[] = [].concat(this.columns)
  public searchFilterString: string;
  public searchValue: string;
  filterString: string;
  sortString: string;
  getWorkLocationsRequest: any;
  pageSize: number = 20;
  skip: number = 0;
  filterToggleDetails: {
    text: string,
    checked: boolean
  } = {
    text: 'FilterInactive',
    checked: true
  }
  public gridDataResult: GridDataResult;
  isLoadingWorkLocations: boolean;
  companyLogoImage: string;
  isLoadingImage: boolean;

  fileInputFormId: string ='frm_BiyrhWjHEwhBo3';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private fb: UntypedFormBuilder,
    private overlayService: OverlayService,
    private snackbarService: SnackbarService,
    private companyLogosService: CompanyLogosService,
    private workLocationsService: WorkLocationsService,
  ) { }

  ngOnInit(): void {
    this.companyLogoId = this.getIdInURL();
    if(this.companyLogoId){
      this.getCompanyLogo();
      this.getWorkLocations();
    }
  }

  getIdInURL(): string {
    let IdInURL: string;

    this.route.paramMap.subscribe(
        params => IdInURL = params.get('id')
    );

    return IdInURL;
  }

  getCompanyLogo() {
    this.isLoading = true;

    this.companyLogosService.getCompanyLogo(this.getIdInURL())
    .pipe(
      finalize( () => {
        this.getCompanyLogoImage();
      })
    )
    .subscribe(
      res => {
        this.companyLogo = res;
      }
    );
  }

  getCompanyLogoImage() {
    this.isLoadingImage = true;

    this.companyLogosService.getCompanyLogoImage(this.getIdInURL())
    .pipe(
      finalize( () => {
        this.buildFormData();
        this.isLoadingImage = false;
      })
    )
    .subscribe(
      res => {
        this.companyLogoImage = res;
      }
    );
  }

  buildFormData(){
    this.isLoading = true;

    this.formData = {
      id: this.companyLogo ? this.companyLogo.id : null,
      name: this.companyLogo ? this.companyLogo.name : null,
      defaultLogo: this.companyLogo ? this.companyLogo.defaultLogo : null,
      active: this.companyLogo ? this.companyLogo.active : null,
      file: this.companyLogoImage ? base64ToFile(this.companyLogoImage) : null,
    }

    this.isLoading = false;
  }
  
  formDataEmitted(formData) {
    this.save(formData);
  }

  formStatusUpdated(formValid) {
    this.formValid = formValid;
  }

  save(formData) {
    if(this.companyLogoId) {
      this.editCompanyLogo(formData);
    }
    else {
      this.createCompanyLogo(formData);
    }
  }

  createCompanyLogo(formData) {
    this.overlayService.show()

    formData.id = this.getIdInURL();
    formData.asOf = moment().format('YYYY-MM-DD'),
    formData.changeReason = null,
    formData.changeReasonComments = null,
    formData.originalCreatedOn = moment().format('YYYY-MM-DD'),

    this.companyLogosService.postCompanyLogo(formData)
    .pipe(
      finalize(() => this.overlayService.hide())
    )
    .subscribe(
      (res) => {
        this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
        this.router.navigate([`${routes.SITE_SETTINGS}${routes.STYLING}${routes.COMPANY_LOGOS}${routes.EDITOR}/${res.id}`]);
      }
    );
  }

  editCompanyLogo(formData) {
    this.overlayService.show()

    formData.id = this.getIdInURL();
    formData.asOf = moment().format('YYYY-MM-DD'),
    formData.changeReason = null,
    formData.changeReasonComments = null,
    formData.originalCreatedOn = this.companyLogo.version?.createdOn,

    this.companyLogosService.putCompanyLogoDetails(this.getIdInURL(), formData)
    .pipe(
      finalize(() => this.overlayService.hide())
    )
    .subscribe(
      (res) => {
        this.snackbarService.openSnackBar(`${this.translate.instant('SavedSuccessfully')}`, 'clear', 'success');
      }
    );
  }

  close(){
    this.router.navigate([`${routes.SITE_SETTINGS}${routes.STYLING}${routes.COMPANY_LOGOS}`]);
  }

  getWorkLocations(){
    this.isLoadingWorkLocations = true;

    let filter: string = `(companyLogo.Id = "${this.companyLogoId}")`;
    
    if(this.searchFilterString) {
      filter = `${filter} AND ${this.searchFilterString}`;
    }

    if(this.filterString) {
      if(filter){
        filter = `${filter} AND ${this.filterString}`;
      }
      else {
        filter = `${this.filterString}`;
      }
    }

    this.getWorkLocationsRequest = this.workLocationsService.getWorkLocations(this.pageSize, this.skip, filter, this.sortString, this.filterToggleDetails.checked)
    .pipe(
        finalize(()=>{ this.isLoadingWorkLocations = false; })
    )
    .subscribe(
      res => {
        this.gridDataResult = {
          data: res.data,
          total: res.total,
        }
      }
    )
  }

  public pageChange(event: PageChangeEvent): void {
    this.skip = event.skip;
    this.pageSize = event.take
    this.getWorkLocations();
  }

  filterCallback(filterString: string) {
    this.filterString = filterString;
    this.skip = 0;;
    this.getWorkLocations();
  }


  search(searchValue) {
    this.searchValue = searchValue;
    this.searchFilterString = `(Name like "${this.searchValue}")`;
    this.getWorkLocationsRequest?.unsubscribe();
    this.getWorkLocations();
  }

  updateFilterToggleDetails(toggleChange: MatSlideToggleChange){
    this.filterToggleDetails.checked = toggleChange.checked;
    this.getWorkLocations();
  }

  sortChange(sortString: string){
    this.sortString = sortString;
    this.getWorkLocations();
  }


}
